import { FC, useEffect, useMemo, useState } from "react";
import { IEsimTariffRate } from "../../../interfaces/esim/EsimMargin.types";
import { eurPerGb } from "../../../utils/esim/margin";
import { foldSearchText } from "../../../utils/searchText";
import { WithTip } from "../../ui/InfoTip";
import NotfoundTable from "../../ui/NotfoundTable";
import Table, { Row } from "../../ui/table";
import Pagination from "../../ui/table/Pagination";

const PAGE_SIZE = 50;

// Only data is sold, so only the data rate is shown; calls and SMS stay in the API answer. Only active
// rules are listed: an inactive one prices nothing, so it is noise next to the ones that bill us.
const COLUMNS = [
  "Shteti",
  "Operatori",
  <WithTip key="cost" label="Kosto për GB" tip="Çmimi që operatori na faturon për çdo GB në këtë rrjet." />,
  "Sponsor",
  "TADIG"
];

interface Props {
  rates: IEsimTariffRate[];
  loading: boolean;
}

const TariffTable: FC<Props> = ({ rates, loading }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = foldSearchText(search);
    const active = rates.filter((r) => r.active);
    const rows = term
      ? active.filter(
          (r) =>
            foldSearchText(r.operatorName ?? "").includes(term) ||
            foldSearchText(r.countryName ?? "").includes(term) ||
            foldSearchText(r.countryIso2 ?? "") === term ||
            foldSearchText(r.tadigs ?? "").includes(term)
        )
      : active;
    // Dearest first: the networks that decide a package's worst case.
    return [...rows].sort((a, b) => (b.dataRatePerGb ?? -1) - (a.dataRatePerGb ?? -1));
  }, [rates, search]);

  useEffect(() => setPage(1), [filtered]);

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kërko operator, shtet ose TADIG"
          className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm
            focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
        />
        <span className="text-xs text-neutral whitespace-nowrap">{filtered.length} rrjete aktive</span>
      </div>
      <Table titles={COLUMNS} loading={loading} style="hover">
        {pageRows.map((r) => (
          <Row
            key={r.ocsRuleId}
            country={
              <>
                {r.countryName ?? "–"}{" "}
                <span className="text-neutral text-xs">{r.countryIso2 ?? ""}</span>
              </>
            }
            operator={
              <span className="inline-block max-w-sm truncate align-bottom" title={r.operatorName ?? ""}>
                {r.operatorName ?? "–"}
              </span>
            }
            cost={<span className="font-medium">{eurPerGb(r.dataRatePerGb)}</span>}
            sponsor={r.sponsorName ?? "–"}
            tadigs={
              <span className="inline-block max-w-xs truncate align-bottom" title={r.tadigs ?? ""}>
                {r.tadigs ?? "–"}
              </span>
            }
          />
        ))}
      </Table>
      {!loading && filtered.length === 0 && (
        <NotfoundTable header="Nuk ka rrjete" paragraph="Asnjë rrjet nuk përputhet me filtrat." />
      )}
      <Pagination
        totalCount={filtered.length}
        pageSize={PAGE_SIZE}
        page={page}
        length={pageRows.length}
        onPageChange={({ selected }) => setPage(selected + 1)}
      />
    </>
  );
};

export default TariffTable;
